import { createTheme } from '@mui/material/styles';
import { alpha, cssVarMap, shellBackdrop, tokens } from './tokens';

/**
 * Light-card theme on a dark shell (login pattern app-wide).
 * Paper/cards = white + dark text. Shell chrome overrides use onShell.
 */
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: tokens.primary,
      dark: tokens.primaryDark,
      light: tokens.primaryLight,
      contrastText: tokens.onPrimary,
    },
    secondary: {
      main: tokens.brandFill,
      dark: tokens.brandInk,
      light: tokens.brandSoft,
      contrastText: '#FFFFFF',
    },
    error: {
      main: tokens.danger,
    },
    success: {
      main: tokens.success,
    },
    info: {
      main: tokens.brandFill,
    },
    warning: {
      main: tokens.warning,
    },
    common: {
      black: tokens.shellDeep,
      white: '#FFFFFF',
    },
    background: {
      default: tokens.canvas,
      paper: tokens.surface,
    },
    text: {
      primary: tokens.ink,
      secondary: tokens.inkMuted,
    },
    divider: tokens.border,
    action: {
      hover: alpha(tokens.primary, 0.06),
      selected: alpha(tokens.primary, 0.1),
    },
  },
  typography: {
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    htmlFontSize: 16,
    fontSize: 16,
    h1: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.75rem, 4vw, 2.75rem)',
      lineHeight: 1.2,
    },
    h2: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.5rem, 3.2vw, 2.15rem)',
      lineHeight: 1.25,
    },
    h3: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.3rem, 2.6vw, 1.75rem)',
      lineHeight: 1.3,
    },
    h4: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.2rem, 2.2vw, 1.5rem)',
      lineHeight: 1.3,
    },
    h5: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.125rem, 2vw, 1.35rem)',
      lineHeight: 1.3,
    },
    h6: {
      fontFamily: '"Fraunces", Georgia, serif',
      fontWeight: 650,
      fontSize: 'clamp(1.05rem, 1.8vw, 1.2rem)',
      lineHeight: 1.35,
    },
    body1: {
      fontSize: '1rem',
      lineHeight: 1.6,
    },
    body2: {
      fontSize: '0.9375rem',
      lineHeight: 1.55,
    },
    caption: {
      fontSize: '0.8125rem',
      lineHeight: 1.45,
    },
    subtitle1: {
      fontSize: '1rem',
      lineHeight: 1.5,
    },
    subtitle2: {
      fontSize: '0.9375rem',
      lineHeight: 1.5,
    },
    button: {
      textTransform: 'none',
      fontWeight: 600,
      fontSize: '0.9375rem',
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        ':root': {
          ...cssVarMap(),
          '--safe-bottom': 'env(safe-area-inset-bottom, 0px)',
          '--safe-top': 'env(safe-area-inset-top, 0px)',
          '--safe-left': 'env(safe-area-inset-left, 0px)',
          '--safe-right': 'env(safe-area-inset-right, 0px)',
        },
        html: {
          backgroundColor: tokens.canvas,
          maxWidth: '100%',
          overflowX: 'clip',
        },
        body: {
          ...shellBackdrop,
          color: tokens.onShell,
          fontSize: '16px',
          lineHeight: 1.5,
          margin: 0,
          maxWidth: '100%',
          overflowX: 'clip',
        },
        '#root': {
          maxWidth: '100%',
          overflowX: 'clip',
        },
        /* Inherit from the surface (shell = light text, cards = ink).
           A global link color was painting dark/teal labels on the dark drawer. */
        a: {
          color: 'inherit',
        },
        'a:visited': {
          color: 'inherit',
        },
      },
    },
    MuiButton: {
      defaultProps: {
        disableElevation: true,
      },
      styleOverrides: {
        root: {
          borderRadius: 10,
          fontWeight: 600,
          minHeight: 44,
        },
        sizeSmall: {
          minHeight: 40,
          fontSize: '0.875rem',
        },
        containedPrimary: {
          backgroundColor: tokens.primary,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: tokens.primaryDark,
            color: '#FFFFFF',
          },
        },
        containedSecondary: {
          backgroundColor: tokens.brandFill,
          color: '#FFFFFF',
          '&:hover': {
            backgroundColor: tokens.brandInk,
            color: '#FFFFFF',
          },
        },
        outlined: {
          borderColor: tokens.border,
          backgroundColor: tokens.surface,
          color: tokens.ink,
          '&:hover': {
            borderColor: tokens.borderHover,
            backgroundColor: tokens.primarySoft,
            color: tokens.ink,
          },
        },
        outlinedInherit: {
          borderColor: alpha('#FFFFFF', 0.28),
          backgroundColor: 'transparent',
          color: tokens.onShell,
          '&:hover': {
            borderColor: alpha('#FFFFFF', 0.45),
            backgroundColor: alpha('#FFFFFF', 0.1),
            color: tokens.onShell,
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          maxWidth: '100%',
          borderRadius: 14,
          border: `1px solid ${tokens.border}`,
          boxShadow: `0 16px 40px ${alpha(tokens.shellDeep, 0.28)}`,
          backgroundImage: 'none',
          backgroundColor: tokens.surface,
          color: tokens.ink,
          /* Keep readable ink even when page chrome uses onShell text. */
          '& .MuiTypography-colorTextPrimary': {
            color: tokens.ink,
          },
          '& .MuiTypography-colorTextSecondary': {
            color: tokens.inkMuted,
          },
          '& .MuiTableCell-root': {
            color: tokens.ink,
          },
          '& .MuiTableCell-head': {
            color: tokens.inkMuted,
          },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
          /* Drawer + AppBar are dark chrome. Do not force white paper / ink
             text onto them — that race made nav labels vanish on iOS. */
          '&:not(.MuiDrawer-paper):not(.MuiAppBar-root)': {
            backgroundColor: tokens.surface,
            color: tokens.ink,
            '& .MuiTypography-colorTextPrimary': {
              color: tokens.ink,
            },
            '& .MuiTypography-colorTextSecondary': {
              color: tokens.inkMuted,
            },
          },
        },
        rounded: {
          borderRadius: 14,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 600,
          fontSize: '0.8125rem',
          height: 32,
        },
        sizeSmall: {
          height: 30,
          fontSize: '0.8125rem',
        },
        filled: {
          backgroundColor: tokens.primarySoft,
          color: tokens.ink,
        },
        outlined: {
          borderColor: tokens.borderHover,
          backgroundColor: tokens.surface,
          color: tokens.ink,
        },
        colorPrimary: {
          backgroundColor: tokens.primarySoft,
          color: tokens.primary,
          borderColor: 'transparent',
        },
        filledPrimary: {
          backgroundColor: tokens.brandFill,
          color: '#FFFFFF',
          '& .MuiChip-label': {
            color: '#FFFFFF',
          },
          '&:hover': {
            backgroundColor: tokens.brandInk,
          },
        },
        outlinedPrimary: {
          backgroundColor: tokens.surface,
          color: tokens.primary,
          borderColor: tokens.borderHover,
        },
        colorSecondary: {
          backgroundColor: tokens.primarySoft,
          color: tokens.brandInk,
        },
        filledSecondary: {
          backgroundColor: tokens.brandFill,
          color: '#FFFFFF',
          '& .MuiChip-label': {
            color: '#FFFFFF',
          },
          '&:hover': {
            backgroundColor: tokens.brandInk,
          },
        },
        outlinedSecondary: {
          backgroundColor: 'transparent',
          color: tokens.brandInk,
          borderColor: tokens.brandFill,
          '& .MuiChip-label': {
            color: 'inherit',
          },
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          backgroundImage: 'none',
          backgroundColor: tokens.canvas,
          borderColor: 'transparent',
          color: tokens.onShell,
          colorScheme: 'dark',
          '& .MuiListItemButton-root, & .MuiListItemButton-root:link, & .MuiListItemButton-root:visited': {
            color: tokens.onShell,
            textDecoration: 'none',
          },
        },
      },
    },
    MuiAppBar: {
      defaultProps: {
        elevation: 0,
        square: true,
      },
      styleOverrides: {
        root: {
          // Sticky headers need an opaque shell fill (set per AppShell).
          // Do not force transparency here — scrolled content would bleed through.
          color: tokens.onShell,
          colorScheme: 'dark',
          boxShadow: 'none',
          border: 'none',
          borderRadius: 0,
          '& .MuiTypography-root': {
            color: tokens.onShell,
          },
        },
      },
    },
    MuiDivider: {
      styleOverrides: {
        root: {
          // Default for light surfaces (cards/tables). Shell chrome sets its own.
          borderColor: tokens.border,
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface,
          color: tokens.ink,
          borderRadius: 14,
          border: `1px solid ${tokens.border}`,
          boxShadow: `0 16px 40px ${alpha(tokens.shellDeep, 0.22)}`,
        },
      },
    },
    MuiTable: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface,
          color: tokens.ink,
        },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        root: {
          color: tokens.ink,
          borderColor: tokens.border,
          fontSize: '0.9375rem',
        },
        head: {
          color: tokens.inkMuted,
          fontWeight: 700,
          backgroundColor: tokens.surface,
        },
        body: {
          color: tokens.ink,
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&.Mui-selected, &.Mui-selected:hover': {
            backgroundColor: alpha(tokens.brand, 0.1),
          },
          '&:hover': {
            backgroundColor: alpha(tokens.primary, 0.04),
          },
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          color: tokens.onShell,
          '& a, &:link, &:visited, &:hover, &:active': {
            color: tokens.onShell,
          },
          '& .MuiListItemIcon-root': {
            color: tokens.onShellMuted,
          },
          '&:hover': {
            backgroundColor: alpha('#FFFFFF', 0.1),
          },
          '&.Mui-selected': {
            backgroundColor: alpha('#FFFFFF', 0.12),
            color: tokens.onShell,
            '&:hover': {
              backgroundColor: alpha('#FFFFFF', 0.16),
            },
            '& .MuiListItemIcon-root': {
              color: tokens.brand,
            },
            '& .MuiListItemText-primary': {
              color: tokens.onShell,
              fontWeight: 700,
            },
          },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: tokens.surface,
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.border,
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.borderHover,
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: tokens.brandFill,
          },
        },
        input: {
          fontSize: '1rem',
          '&::placeholder': {
            color: tokens.inkMuted,
            opacity: 1,
          },
        },
        notchedOutline: {
          // Ensure the label notch cuts cleanly through the border.
          legend: {
            fontSize: '0.75em',
          },
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
        size: 'small',
      },
    },
    MuiFormControl: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: tokens.inkMuted,
          fontWeight: 600,
          fontSize: '0.9375rem',
          '&.Mui-focused': {
            color: tokens.brandInk,
          },
        },
      },
    },
    MuiFormLabel: {
      styleOverrides: {
        root: {
          color: tokens.inkMuted,
        },
      },
    },
    MuiSelect: {
      defaultProps: {
        size: 'small',
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          color: tokens.ink,
        },
        standardError: {
          backgroundColor: '#FDECEC',
          color: tokens.ink,
        },
        standardSuccess: {
          backgroundColor: '#E8F5EF',
          color: tokens.ink,
        },
        standardInfo: {
          backgroundColor: tokens.brandSoft,
          color: tokens.ink,
        },
        standardWarning: {
          backgroundColor: '#FFF6E8',
          color: tokens.ink,
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.surface,
          color: tokens.ink,
          '& .MuiTypography-colorTextPrimary': {
            color: tokens.ink,
          },
          '& .MuiTypography-colorTextSecondary': {
            color: tokens.inkMuted,
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.surface,
          color: tokens.ink,
        },
      },
    },
    MuiPopover: {
      styleOverrides: {
        paper: {
          backgroundColor: tokens.surface,
          color: tokens.ink,
        },
      },
    },
  },
});

export default theme;
